import React, { type JSX } from "react";

import { cn } from "@eisox/design-system";

import Warning from "~/assets/boilerroomWarning.svg?react";
import { m } from "~/paraglide/messages";

import { Tooltip } from "./tooltip";

interface Inconsistency<T extends { name?: string }, K extends keyof T> {
  items: T[];
  property: K;
  formatValue?: (value: T[K]) => React.ReactNode;
  type: "boiler_room" | "air_handler_unit";
}

export const Inconsistency = <T extends { name?: string }, K extends keyof T>({
  items,
  property,
  formatValue,
  type,
}: Inconsistency<T, K>): JSX.Element | null => {
  const result = checkInconsistencies(items, property);

  if (!result?.inconsistency) return null;

  const renderValue = formatValue ?? (val => String(val ?? "-"));

  return (
    <Tooltip
      content={
        <div className={cn("[&>p:first-child]:mb-[10px]!")}>
          <p>{m[`${type}_inconsistency`]()}</p>
          <ul>
            {Array.from(result.inconsistency).map(([name, value]) => (
              <li key={name}>
                <strong>{name}:</strong> {renderValue(value)}
              </li>
            ))}
          </ul>
        </div>
      }
    >
      <Warning className={cn("h-[17px] w-[17px]")} />
    </Tooltip>
  );
};

Inconsistency.displayName = "Inconsistency";

const checkInconsistencies = <T extends { name?: string }, K extends keyof T>(items: T[], property: K) => {
  if (!items.length) return null;
  const firstItem = items[0];
  if (!firstItem) return null;
  const value = firstItem[property];
  const hasError = items.some(i => i[property] !== value);

  return {
    property,
    value,
    inconsistency: hasError ? new Map(items.map(i => [i.name ?? "", i[property]])) : null,
  };
};
